import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import {
  RubricType,
  RubricTypeList,
  RubricTypeMap,
} from '@breakoutlearning/firebase-repository/types'
import { useRepository } from 'hooks/auth'
import type { Translation } from 'i18n/i18n'
import { useTranslationTyped } from 'i18n/i18n'
import { reaction } from 'mobx'
import { useCallback, useEffect, useMemo, useState } from 'react'

type RubricTypeWrapper = {
  type: RubricType
  title: string
  description: string
  minScore: number
  maxScore: number
  excludeSoloExperiences: boolean
  scoreDescriptions: {
    score: number
    title: string
    explanation: string
    prompt: string
    color: string
    textColor?: string
    meaning?: string
  }[]
  scoreGrouping?: {
    [key: string]: {
      color: string
      scores: number[]
    }
  }
  getScoreLabel: (score: number, rubric: SlideRubric) => string
  getScoreDescription: (score: number) => string
  getScoreMeaning: (score: number) => string
  getScoreExplanation: (score: number) => string
  getScoreBgColor: (score: number, options?: { opacity: number }) => string
  getScoreTextColor: (score: number) => string | undefined
}

// const getBloomLongDescription = (t: Translation, score: number) => {
//   switch (score) {
//     case 0:
//       return t('instructor_assignment.bloom_0_long_description')
//     case 1:
//       return t('instructor_assignment.bloom_1_long_description')
//     case 2:
//       return t('instructor_assignment.bloom_2_long_description')
//     case 3:
//       return t('instructor_assignment.bloom_3_long_description')
//     case 4:
//       return t('instructor_assignment.bloom_4_long_description')
//     case 5:
//       return t('instructor_assignment.bloom_5_long_description')
//     case 6:
//       return t('instructor_assignment.bloom_6_long_description')
//     default:
//       return ''
//   }
// }

const bloomsScoreToWhatDoesThisMean = (
  t: (key: string) => string,
  score: number
) => {
  // NOTE(danielmartin0): I'm anti-DRY here because since locale isn't typesafe, we sometimes need to grep the repo for locale strings and find all the places they're used.
  if (score === 0) return t('instructor_assignment.bloom_0_meaning')
  if (score === 1) return t('instructor_assignment.bloom_1_meaning')
  if (score === 2) return t('instructor_assignment.bloom_2_meaning')
  if (score === 3) return t('instructor_assignment.bloom_3_meaning')
  if (score === 4) return t('instructor_assignment.bloom_4_meaning')
  if (score === 5) return t('instructor_assignment.bloom_5_meaning')
  if (score === 6) return t('instructor_assignment.bloom_6_meaning')
}

function buildEmptyWrapper(rubricType: RubricType, t: Translation) {
  const type: RubricTypeWrapper = {
    type: rubricType,
    title: '',
    description: '',
    minScore: 0,
    maxScore: 0,
    excludeSoloExperiences: false,
    scoreDescriptions: [] as {
      color: string
      title: string
      score: number
      explanation: string
      prompt: string
    }[],
    getScoreLabel: function (score: number, rubric: SlideRubric) {
      // special label overrides for pass/fail rubrics
      if (rubric.rubricType === RubricType.passFail) {
        if (score === 0 && rubric.data.rubricLabel0)
          return rubric.data.rubricLabel0
        if (score === 1 && rubric.data.rubricLabel1)
          return rubric.data.rubricLabel1
      }

      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )

      if (scoreDescription) {
        return scoreDescription.title
      }

      return ''
    },
    getScoreDescription: function (score: number) {
      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )
      if (scoreDescription) {
        return scoreDescription.explanation
      }
      return ''
    },
    getScoreMeaning: function (score: number) {
      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )
      if (!scoreDescription) return ''

      if (type.type === RubricType.blooms) {
        return bloomsScoreToWhatDoesThisMean(t, score) || ''
      }

      // DQR types use the meaning field from score descriptions
      const dqrTypes = [
        RubricType.evidence,
        RubricType.peerEngagement,
        RubricType.criticalThinking,
      ]
      if (dqrTypes.includes(type.type)) {
        return scoreDescription.meaning || ''
      }

      return ''
    },
    getScoreExplanation: function (score: number) {
      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )
      return scoreDescription?.explanation ?? ''
    },

    getScoreBgColor: function (score: number, options?: { opacity: number }) {
      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )

      const colorStr = scoreDescription?.color ?? '#000000' //black

      if (options?.opacity)
        return applyOpacityHexOrRGBString(colorStr, options.opacity)

      return colorStr

      // if (type.type === RubricType.blooms) {
      //   switch (score as BloomScore) {
      //     case BloomScore.none:
      //       return 'bg-breakout-color-scale-0'
      //     case BloomScore.remember:
      //       return 'bg-breakout-color-scale-1'
      //     case BloomScore.understand:
      //       return 'bg-breakout-color-scale-2'
      //     case BloomScore.apply:
      //       return 'bg-breakout-color-scale-3'
      //     case BloomScore.analyze:
      //       return 'bg-breakout-color-scale-4'
      //     case BloomScore.evaluate:
      //       return 'bg-breakout-color-scale-5'
      //     case BloomScore.create:
      //       return 'bg-breakout-color-scale-6'
      //     default:
      //       return 'bg-breakout-color-scale-0'
      //   }
      // }
      // switch (score as PassFailScore) {
      //   case PassFailScore.fail:
      //     return 'bg-breakout-red'
      //   case 1:
      //     return 'bg-breakout-dark-green'
      //   default:
      //     return 'bg-breakout-red'
      // }
    },

    getScoreTextColor: function (score: number) {
      const scoreDescription = type.scoreDescriptions.find(
        (desc) => desc.score === score
      )

      const color = scoreDescription?.textColor
      return color || undefined
    },
  }

  return type
}

export function getRetiredRubricTypes(): RubricType[] {
  return [RubricType.evidence]
}

// convenience wrapper for rubric types - so that once we need to bring in i18n,
// we'll have a way to access tt - and a single access point
export function useRubricTypes() {
  const repository = useRepository()
  const { tt, t } = useTranslationTyped()

  const calculateTypes = useCallback(() => {
    const types: RubricTypeWrapper[] = []
    RubricTypeList.forEach((rubricType) => {
      // get values from db
      const type = buildEmptyWrapper(rubricType, t)

      const value = repository.rubricTypes.getRubricTypeByTypeId(rubricType)
      if (value) {
        type.title = value.title
        type.description = value.description
        type.minScore = value.minScore
        type.maxScore = value.maxScore
        type.scoreDescriptions = value.scoreDescriptions
        type.scoreGrouping = value.scoreGrouping
        type.excludeSoloExperiences = value.excludeSoloExperiences
      }
      switch (rubricType) {
        case RubricType.passFail:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_pass_fail()
          }
          break
        case RubricType.blooms:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_blooms()
          }
          break
        case RubricType.decisionMaking:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_decision_making()
          }
          break
        case RubricType.evidence:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_evidence()
          }
          break
        case RubricType.peerEngagement:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_peer_engagement()
          }
          break
        case RubricType.criticalThinking:
          if (!type.title) {
            type.title = tt.rubric.rubric_type_critical_thinking()
          }
          break
        default:
        // pass
      }

      types.push(type)
    })
    return types
  }, [t, repository.rubricTypes, tt.rubric])

  const initialTypes: RubricTypeWrapper[] = useMemo(() => {
    return calculateTypes()
  }, [calculateTypes])

  const [types, setTypes] = useState<RubricTypeWrapper[]>(initialTypes)

  // should the collection change, re-calculate the types
  useEffect(() => {
    return reaction(
      () => repository.rubricTypes._promptTypes.models,
      () => setTypes(calculateTypes())
    )
  }, [repository, calculateTypes])

  const typesById = useMemo(() => {
    // @ts-expect-error I know, but this is a workaround for now
    const typesById: Record<RubricType, RubricTypeWrapper> = {}
    for (const type of types) {
      typesById[type.type] = type
    }
    return typesById
  }, [types])

  const typesByTypeName = useMemo(() => {
    const typesByName: Record<string, RubricTypeWrapper> = {}
    Object.entries(RubricTypeMap).map(([key, type]) => {
      typesByName[key] = typesById[type]
    })
    return typesByName
  }, [typesById])

  const rubricTypesWithDatabaseRecord = useMemo(() => {
    return types.filter((type) =>
      repository.rubricTypes._promptTypes.models.some(
        (promptType) => Number(promptType.id) === type.type
      )
    )
  }, [types, repository.rubricTypes._promptTypes])

  const getRubricByType = useCallback(
    (rubricType: RubricType | undefined) => {
      if (rubricType === undefined)
        return buildEmptyWrapper(RubricType.blooms, t)
      return typesById[rubricType] || buildEmptyWrapper(rubricType, t)
    },
    [t, typesById]
  )

  return {
    rubricTypes: types,
    rubricTypesById: typesById,
    rubricTypesByName: typesByTypeName,
    rubricTypesWithDatabaseRecord,
    getRubricByType: getRubricByType,
    blooms: typesByTypeName.blooms || buildEmptyWrapper(RubricType.blooms, t),
    passFail:
      typesByTypeName.passFail || buildEmptyWrapper(RubricType.passFail, t),
    decisionMaking:
      typesByTypeName.decisionMaking ||
      buildEmptyWrapper(RubricType.decisionMaking, t),
    evidence:
      typesByTypeName.evidence || buildEmptyWrapper(RubricType.evidence, t),
    peerEngagement:
      typesByTypeName.peerEngagement ||
      buildEmptyWrapper(RubricType.peerEngagement, t),
    criticalThinking:
      typesByTypeName.criticalThinking ||
      buildEmptyWrapper(RubricType.criticalThinking, t),
  }
}

function applyOpacityHexOrRGBString(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    // Handle hex (e.g., "#eda247")
    const hex = color.replace('#', '')

    // Expand shorthand hex (#abc -> #aabbcc)
    const fullHex =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex

    const r = parseInt(fullHex.substring(0, 2), 16)
    const g = parseInt(fullHex.substring(2, 4), 16)
    const b = parseInt(fullHex.substring(4, 6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  if (color.startsWith('rgba')) {
    return color.replace(/rgba\(([^)]+),\s*[\d.]+\)/, `rgba($1, ${alpha})`)
  }

  if (color.startsWith('rgb')) {
    return color.replace(/rgb\(([^)]+)\)/, `rgba($1, ${alpha})`)
  }

  throw new Error(`Unsupported color format: ${color}`)
}
